import { useEffect, useState } from "react";
import { takeReturnTo } from "../../routes/returnTo";
import { useTranslation } from "react-i18next";
import { postData } from "../../function/FunctionApi";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  ChartColumnIncreasing,
  CircleHelp,
  Eye,
  EyeOff,
  Globe,
  Handshake,
  Headphones,
  Lock,
  Settings,
  ShieldCheck,
  User,
} from "lucide-react";
import logo from "../../Image/fLogo.png";
import heroImage from "../../assets/login-hero.webp";
import {
  setUserSession,
  getUserSession,
  isUserSessionValid,
} from "../../function/AuthStorage";
import "./Login.css";

/** قيم الشركة على صورة الواجهة — بالترتيب من اليسار كما في التصميم. */
const VALUES = [
  { key: "safety", Icon: ShieldCheck, tone: "gold" },
  { key: "quality", Icon: Settings, tone: "blue" },
  { key: "commitment", Icon: Handshake, tone: "gold" },
  { key: "excellence", Icon: ChartColumnIncreasing, tone: "blue" },
];

/*
  روابط أسفل لوح الدخول. عناوينها تُضبط من متغيرات البيئة عند النشر
  (REACT_APP_SUPPORT_URL ...). ما لم يُضبط له عنوان يظهر بلا رابط
  بدل أن يقود إلى صفحة غير موجودة.
*/
const LINKS = [
  { key: "privacy", Icon: ShieldCheck, href: process.env.REACT_APP_PRIVACY_URL },
  { key: "policies", Icon: BookOpen, href: process.env.REACT_APP_POLICIES_URL },
  { key: "guide", Icon: CircleHelp, href: process.env.REACT_APP_USER_GUIDE_URL },
  { key: "support", Icon: Headphones, href: process.env.REACT_APP_SUPPORT_URL },
];

/*
  الدخول بحساب الشركة (Microsoft) لا يظهر إلا إذا فُعِّل بضبط
  REACT_APP_MS_LOGIN_URL — لا يوجد تكامل مع مزوّد هوية في الخادم بعد،
  وزرٌّ ظاهر لا يعمل أسوأ من غيابه.
*/
const MS_LOGIN_URL = process.env.REACT_APP_MS_LOGIN_URL;

// ─── Mapping: permission → أول route مناسب ────────────────────────────────
const PERM_ROUTE_MAP = [
  { perm: "Construction.View", route: "/search-requests" },
  { perm: "Emergency.View", route: "/search-requests" },
  { perm: "Maintenance.View", route: "/search-requests" },
  { perm: "NewProject.View", route: "/search-requests" },
  { perm: "PrivateProject.View", route: "/all-admin-private-project" },
  { perm: "Users.View", route: "/accounts" },
  { perm: "Employees.View", route: "/employees" },
  { perm: "Branch.View", route: "/branches" },
  { perm: "Office.View", route: "/offices" },
  { perm: "Custody.View", route: "/custodies" },
  { perm: "Attendance.View", route: "/check-attendance" },
  { perm: "LeaveRequest.View", route: "/vacations" },
  { perm: "Notifications.View", route: "/notification" },
  { perm: "Neighborhood.View", route: "/district" },
  { perm: "Consultant.View", route: "/consultants" },
  { perm: "WorkOrderType.View", route: "/tasktype" },
];

function getRedirectAfterLogin(userData) {
  const { userType, permissions = [] } = userData;

  if (userType === "admin") return "/home-page";
  if (userType === "eng") return "/main-page";
  if (userType === "contractor") return "/projects";
  // الرئيسية (لوحة المؤشرات العامة) لمن يملك صلاحيتها، وإلا أول صفحة مسموحة له
  if (permissions.includes("Dashboard.View")) return "/home-page";

  for (const { perm, route } of PERM_ROUTE_MAP) {
    if (permissions.includes(perm)) return route;
  }

  if (userType === "supervisor" || userType === "officeManager") return "/profile";
  return "/no-access";
}

function Login({ setUserData }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "ar").split("-")[0];
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  /*
    «تذكرني» يحفظ اسم المستخدم وحده — لا كلمة المرور.

    حفظ الكلمة في المتصفّح يجعل من يصل إلى الجهاز يصل إلى الحساب، وهذه
    الخانة في أكثر الأنظمة لا تحفظ إلا الاسم.
  */
  const [remember, setRemember] = useState(
    () => !!localStorage.getItem("asf.rememberedUser")
  );

  const navigate = useNavigate();

  const [data, setData] = useState({
    UserName: localStorage.getItem("asf.rememberedUser") || "",
    Password: "",
  });

  // من له جلسة صالحة يُوجَّه إلى صفحته. التوجيه إلى "/" كان لا يفعل شيئاً
  // لأن صفحة الدخول نفسها هي "/"، فيبقى المستخدم المسجّل أمام نموذج الدخول.
  useEffect(() => {
    if (isUserSessionValid()) {
      navigate(takeReturnTo() || getRedirectAfterLogin(getUserSession()), { replace: true });
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { id, value } = e.target;

    setData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
  };

  /**
   * يقرأ الموقع إن أمكن، ويعيد null إن تعذّر.
   *
   * بمهلة: المتصفّح لا يُحدّد وقتاً لانتظار جواب المستخدم، فبلا مهلة يبقى
   * الانتظار إلى الأبد.
   */
  const readLocation = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }

      let settled = false;
      const finish = (value) => {
        if (settled) return;
        settled = true;
        resolve(value);
      };

      const timer = setTimeout(() => finish(null), 8000);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          clearTimeout(timer);
          finish({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        () => {
          clearTimeout(timer);
          finish(null);
        },
        { timeout: 8000, maximumAge: 300000 }
      );
    });

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!data.UserName.trim() || !data.Password) {
      setError(t("login.errors.required"));
      return;
    }

    setLoading(true);
    setError("");

    try {
      // الموقع محاولةٌ لا شرط.
      //
      // كان الدخول ينتظر إذن الموقع: من رفضه لم يدخل، ومن لم يجب على السؤال
      // بقي أمام شاشة صامتة بلا رسالة — الوعد لا يُحسم فلا يُرسل الطلب ولا
      // يظهر خطأ. والموقع لا يُستعمل في الدخول أصلاً، بل في إجراء واحد لاحق
      // يتحقّق منه بنفسه ويقول «لم يتم العثور على الموقع».
      const location = await readLocation();
      if (location) localStorage.setItem("userLocation", JSON.stringify(location));

      // البيانات في جسم الطلب لا في الرابط: كلمة المرور في سلسلة الاستعلام
      // تُسجَّل في سجلّ الخادم وسجلّ المتصفّح وأيّ وسيط بينهما.
      const result = await postData("Account/login", data, () => {});

      if (result) {
        // عدم تخزين الباسورد على الفرونت
        const { password, ...safeUserData } = result.data;

        const saved = setUserSession(safeUserData);

        if (!saved) {
          setError(t("login.errors.session"));
          return;
        }

        setUserData(safeUserData);

        // الاسم وحده يُحفظ، والكلمة لا تُحفظ أبداً.
        try {
          if (remember) localStorage.setItem("asf.rememberedUser", data.UserName);
          else localStorage.removeItem("asf.rememberedUser");
        } catch {
          // التخزين قد يكون محجوباً — ولا يمنع الدخول.
        }

        const userType = safeUserData.userType;

        localStorage.setItem("userType", userType);
        localStorage.setItem(
          "IsRiyadh",
          safeUserData.branchName !== "جدة"
        );
        localStorage.setItem(
          "isAllowed",
          safeUserData.userCanCreateProjectOutsideCityType
        );
        localStorage.setItem("branchId", safeUserData.branchId);

        navigate(takeReturnTo() || getRedirectAfterLogin(safeUserData));
      }
    } catch (error) {
      const status = error.response?.status;
      if (!error.response) {
        setError(t("login.errors.network"));
      } else if (status === 400 || status === 401) {
        setError(t("login.errors.invalid"));
      } else {
        // رسالة الخادم عربية — تُعرض كما هي في الواجهة العربية فقط
        const serverMessage = error.response?.data?.message;
        setError(lang === "ar" && serverMessage ? serverMessage : t("login.errors.generic"));
      }
    } finally {
      setLoading(false);
    }
  };

  const year = new Date().getFullYear();

  return (
    <div className="lg">
      {/* ─────────── الصورة والهوية ─────────── */}
      <div
        className="lg__bg"
        style={{ backgroundImage: `url("${heroImage}")` }}
        aria-hidden="true"
      />

      <section className="lg__hero">
        <div className="lg__heroInner">
          <img src={logo} alt={t("login.heroAlt")} className="lg__logo" />

          <h1 className="lg__slogan">
            <span className="lg__slogan1">{t("login.slogan1")}</span>
            <span className="lg__slogan2">{t("login.slogan2")}</span>
          </h1>

          <p className="lg__pitch">{t("login.pitch")}</p>
        </div>

        <div className="lg__heroBottom">
          <ul className="lg__values">
            {VALUES.map(({ key, Icon, tone }) => (
              <li key={key} className="lg__value">
                <Icon
                  className={`lg__valueIcon lg__valueIcon--${tone}`}
                  strokeWidth={2}
                  aria-hidden="true"
                />
                <strong>{t(`login.values.${key}.title`)}</strong>
                <span>{t(`login.values.${key}.note`)}</span>
              </li>
            ))}
          </ul>

          <p className="lg__tagline" dir="ltr">
            {t("login.tagline")}
          </p>
        </div>
      </section>

      {/* ─────────── لوح الدخول ─────────── */}
      <section className="lg__panel">
        <div className="lg__lang">
          <Globe className="lg__langIcon" strokeWidth={1.8} aria-hidden="true" />
          <button
            type="button"
            className={`lg__langBtn ${lang === "ar" ? "lg__langBtn--on" : ""}`}
            aria-pressed={lang === "ar"}
            onClick={() => i18n.changeLanguage("ar")}
          >
            العربية
          </button>
          <button
            type="button"
            className={`lg__langBtn ${lang === "en" ? "lg__langBtn--on" : ""}`}
            aria-pressed={lang === "en"}
            onClick={() => i18n.changeLanguage("en")}
          >
            English
          </button>
        </div>

        <div className="lg__form">
          <h2 className="lg__title">{t("login.title")}</h2>
          <p className="lg__sub">{t("login.subtitle")}</p>

          <form onSubmit={handleFormSubmit} noValidate>
            <div className="lg__field">
              <label htmlFor="UserName">{t("login.username")}</label>
              {/* اسم المستخدم وكلمة المرور لاتينية، فالحقل من اليسار كما في التصميم */}
              <div className="lg__input" dir="ltr">
                <User className="lg__inputIcon" strokeWidth={1.8} aria-hidden="true" />
                <input
                  type="text"
                  id="UserName"
                  value={data.UserName}
                  onChange={handleChange}
                  disabled={loading}
                  autoComplete="username"
                  placeholder={t("login.username")}
                />
              </div>
            </div>

            <div className="lg__field">
              <label htmlFor="Password">{t("login.password")}</label>
              <div className="lg__input" dir="ltr">
                <Lock className="lg__inputIcon" strokeWidth={1.8} aria-hidden="true" />
                <input
                  type={showPassword ? "text" : "password"}
                  id="Password"
                  value={data.Password}
                  onChange={handleChange}
                  disabled={loading}
                  autoComplete="current-password"
                  placeholder={t("login.password")}
                />
                <button
                  type="button"
                  className="lg__eye"
                  onClick={() => setShowPassword((prev) => !prev)}
                  disabled={loading}
                  aria-label={showPassword ? t("login.hidePassword") : t("login.showPassword")}
                >
                  {showPassword ? (
                    <EyeOff strokeWidth={1.8} aria-hidden="true" />
                  ) : (
                    <Eye strokeWidth={1.8} aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>

            <div className="lg__row">
              <Link to="/reset-password" className="lg__forgot">
                {t("login.forgot")}
              </Link>

              <label className="lg__remember">
                <span>{t("login.remember")}</span>
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  disabled={loading}
                />
              </label>
            </div>

            <button type="submit" className="lg__submit" disabled={loading}>
              <span>{loading ? t("login.submitting") : t("login.submit")}</span>
              <ArrowRight className="lg__submitIcon" strokeWidth={2} aria-hidden="true" />
            </button>

            {error && (
              <div className="lg__error" role="alert">
                {error}
              </div>
            )}
          </form>

          {MS_LOGIN_URL && (
            <>
              <div className="lg__or">
                <span>{t("login.or")}</span>
              </div>
              <a className="lg__sso" href={MS_LOGIN_URL}>
                <span>{t("login.sso")}</span>
                <MicrosoftMark />
              </a>
            </>
          )}

          <ul className="lg__links">
            {LINKS.map(({ key, Icon, href }) => {
              const content = (
                <>
                  <Icon strokeWidth={1.6} aria-hidden="true" />
                  <span>{t(`login.links.${key}`)}</span>
                </>
              );
              return (
                <li key={key}>
                  {href ? (
                    <a href={href} target="_blank" rel="noopener noreferrer">
                      {content}
                    </a>
                  ) : (
                    <span className="lg__linkStatic">{content}</span>
                  )}
                </li>
              );
            })}
          </ul>

          <p className="lg__copy">{t("login.copyright", { year })}</p>
        </div>
      </section>
    </div>
  );
}

/** شعار مايكروسوفت — أربعة مربّعات، بلا مكتبة أيقونات إضافية. */
function MicrosoftMark() {
  return (
    <span className="lg__ms" aria-hidden="true">
      <i style={{ background: "#f25022" }} />
      <i style={{ background: "#7fba00" }} />
      <i style={{ background: "#00a4ef" }} />
      <i style={{ background: "#ffb900" }} />
    </span>
  );
}

export default Login;
